/**
 * Проверка ответов агента на границе (D-056): агент на компьютере кассира может быть другой версии, поэтому лишних
 * полей не боимся, а отсутствие главных превращаем в понятную ошибку, а не в падение экрана.
 */
import { decodeAtolDriver } from '@krp/client-runtime'

import { isRecord } from '../../../shared/decode'
import { ATOL_DEFAULT_MODEL, ATOL_DEFAULT_PORT } from '../model/kassas'

import type {
  CandidateDevice,
  CandidateState,
  DriverCategory,
  DriverState,
  DriverStatus,
  EquipmentCandidate,
  EquipmentScanResult,
  AtolEquipment,
  AtolSettings,
  DeviceProblem,
  DeviceState,
  KassaConnection,
  TerminalCapabilities,
  TerminalDriver,
  TerminalDriverState,
  TerminalField,
  TerminalItem,
  TerminalPing,
  TerminalsData,
} from '../model/types'

/** Тексты ошибок агента — «<что>: ожидался объект», они закреплены тестами и отличаются от общего `shared/decode`. */
const record = (raw: unknown, what: string): Record<string, unknown> => {
  if (!isRecord(raw)) throw new Error(`${what}: ожидался объект`)
  return raw
}

/** Строка агента: пустая или не строка — `null` (в отличие от `shared/decode.text`, который бросает ошибку). */
const textOrNull = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null)

function decodeSettings(raw: unknown): AtolSettings {
  const value = record(raw, 'настройки кассы')
  return {
    enabled: value.enabled === true,
    model: typeof value.model === 'number' ? value.model : ATOL_DEFAULT_MODEL,
    port: value.port === 0 ? 0 : ATOL_DEFAULT_PORT,
    comFile: textOrNull(value.comFile),
    baudRate: typeof value.baudRate === 'number' ? value.baudRate : null,
  }
}

/** Ответ `GET /v1/equipment` или `PUT /v1/equipment/atol`: одна касса. */
export function decodeAtolEquipment(raw: unknown): AtolEquipment {
  const value = record(raw, 'касса')
  return {
    id: textOrNull(value.id) ?? 'atol-kkt',
    title: textOrNull(value.title) ?? 'Касса АТОЛ',
    driver: decodeAtolDriver(value.driver),
    settings: decodeSettings(value.settings),
    active: value.active === true,
  }
}

export function decodeEquipmentList(raw: unknown): AtolEquipment | null {
  const items: unknown = record(raw, 'список оборудования').items
  if (!Array.isArray(items)) throw new Error('список оборудования: нет items')
  const atol: unknown = items.find((item: unknown) => isRecord(item) && item.id === 'atol-kkt')
  return atol === undefined ? null : decodeAtolEquipment(atol)
}

const CONNECTIONS: readonly KassaConnection[] = ['ok', 'no-device', 'busy', 'driver-missing', 'error']

function decodeProblem(raw: unknown): DeviceProblem {
  const value = record(raw, 'неполадка')
  return {
    code: textOrNull(value.code) ?? 'unknown',
    severity: value.severity === 'warn' ? 'warn' : 'block',
    text: textOrNull(value.text) ?? 'Неполадка кассы.',
    action: textOrNull(value.action),
  }
}

/** Ответ `GET /v1/device`: связь, можно ли продавать, смена и неполадки с советом. */
export function decodeDeviceState(raw: unknown): DeviceState {
  const value = record(raw, 'состояние кассы')
  const connection = CONNECTIONS.find((item) => item === value.connection) ?? 'error'
  const shift = value.shift === undefined || value.shift === null ? null : record(value.shift, 'смена')
  return {
    connection,
    canSell: value.canSell === true,
    shiftState: shift === null ? null : textOrNull(shift.state),
    shiftNumber: shift !== null && typeof shift.number === 'number' ? shift.number : null,
    problems: Array.isArray(value.problems) ? value.problems.map(decodeProblem) : [],
  }
}

// ---- эквайринговые терминалы (агент: docs/DRIVERS.md) ----

const list = (value: unknown, what: string): readonly unknown[] => {
  if (!Array.isArray(value)) throw new Error(`${what}: ожидался список`)
  return value
}

const DRIVER_STATES: readonly TerminalDriverState[] = ['ready', 'not-installed', 'stopped', 'unsupported']

function decodeField(raw: unknown): TerminalField {
  const value = record(raw, 'поле настроек')
  const key = textOrNull(value.key)
  if (key === null) throw new Error('поле настроек: нет key')
  return {
    key,
    title: textOrNull(value.title) ?? key,
    required: value.required === true,
    default: textOrNull(value.default),
    hint: textOrNull(value.hint),
  }
}

function decodeTerminalDriver(raw: unknown): TerminalDriver {
  const value = record(raw, 'драйвер терминалов')
  const kind = textOrNull(value.kind)
  if (kind === null) throw new Error('драйвер терминалов: нет kind')
  const status = record(value.status, 'состояние драйвера')
  return {
    kind,
    title: textOrNull(value.title) ?? kind,
    fields: list(value.fields ?? [], 'поля настроек').map(decodeField),
    // Неизвестное состояние (агент новее окна) читаем как «не поддерживается»: окно не обещает того, чего не знает.
    state: DRIVER_STATES.find((item) => item === status.state) ?? 'unsupported',
    message: textOrNull(status.message) ?? '',
  }
}

function decodeCapabilities(raw: unknown): TerminalCapabilities {
  const value = raw === undefined || raw === null ? {} : record(raw, 'возможности терминала')
  return {
    pay: value.pay === true,
    cancel: value.cancel === true,
    refund: value.refund === true,
    reconcile: value.reconcile === true,
    verified: value.verified === true,
  }
}

function decodeStringMap(raw: unknown): Readonly<Record<string, string>> {
  if (raw === undefined || raw === null) return {}
  const value = record(raw, 'значения')
  return Object.fromEntries(Object.entries(value).filter((entry): entry is [string, string] => typeof entry[1] === 'string'))
}

function decodeTerminalItem(raw: unknown): TerminalItem {
  const value = record(raw, 'терминал')
  const settings = record(value.settings, 'настройки терминала')
  const id = textOrNull(settings.id)
  const kind = textOrNull(settings.kind)
  if (id === null || kind === null) throw new Error('терминал: нет id или kind')
  return {
    id,
    kind,
    title: textOrNull(value.driverTitle) ?? kind,
    active: value.active === true,
    values: decodeStringMap(settings.values),
    capabilities: decodeCapabilities(value.capabilities),
  }
}

/** Ответ `GET /v1/terminals`: драйверы (с состоянием на этом компьютере) и настроенные терминалы. */
export function decodeTerminals(raw: unknown): TerminalsData {
  const value = record(raw, 'терминалы')
  return {
    drivers: list(value.drivers ?? [], 'драйверы').map(decodeTerminalDriver),
    items: list(value.items ?? [], 'терминалы').map(decodeTerminalItem),
  }
}

/** Ответ `PUT /v1/terminals/{id}`, `POST /v1/terminals/{id}/activate`: один терминал. */
export function decodeTerminalSaved(raw: unknown): TerminalItem {
  return decodeTerminalItem(raw)
}

/** Ответ `POST /v1/terminals/{id}/test`. */
export function decodeTerminalPing(raw: unknown): TerminalPing {
  const value = record(raw, 'проверка связи с терминалом')
  return { ok: value.ok === true, message: textOrNull(value.message) ?? (value.ok === true ? 'Терминал отвечает.' : 'Терминал не отвечает.') }
}

// ---- окно «Оборудование», версия 2 (EQW2-W0) ----

const finiteNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null)

const DRIVER_STATES_V2: readonly DriverState[] = ['ready', 'not-installed', 'stopped', 'incompatible', 'unsupported']

function driverCategory(value: unknown): DriverCategory {
  if (value === 'fiscal-register' || value === 'fiscal-printer') return 'fiscal-register'
  if (value === 'payment-terminal') return 'payment-terminal'
  return 'other'
}

/** Ответ `GET /v1/drivers` (п. 7.2). Неизвестное состояние (агент новее окна) читаем как «не поддерживается». */
export function decodeDriversList(raw: unknown): readonly DriverStatus[] {
  const items = list(record(raw, 'список драйверов').drivers ?? [], 'драйверы')
  return items.map((item): DriverStatus => {
    const value = record(item, 'драйвер')
    const id = textOrNull(value.id)
    if (id === null) throw new Error('драйвер: нет id')
    return {
      id,
      category: driverCategory(value.category),
      title: textOrNull(value.title) ?? id,
      vendor: textOrNull(value.vendor),
      state: DRIVER_STATES_V2.find((state) => state === value.state) ?? 'unsupported',
      version: textOrNull(value.version),
      path: textOrNull(value.path),
      message: textOrNull(value.message) ?? '',
    }
  })
}

const CANDIDATE_STATES_V2: readonly CandidateState[] = [
  'ready',
  'needs-input',
  'no-device',
  'busy',
  'driver-missing',
  'service-stopped',
  'unsupported',
  'error',
]

/** Терминальное `no-terminal` в общем списке — то же, что `no-device`; неизвестное — `error`: окно не падает и не врёт. */
function candidateState(value: unknown): CandidateState {
  if (value === 'no-terminal') return 'no-device'
  return CANDIDATE_STATES_V2.find((state) => state === value) ?? 'error'
}

function decodeCandidateDevice(raw: unknown): CandidateDevice | null {
  if (!isRecord(raw)) return null
  const value = raw
  return {
    model: finiteNumber(value.model),
    modelName: textOrNull(value.modelName),
    serial: textOrNull(value.serial),
    firmwareVersion: textOrNull(value.firmwareVersion),
    ffdVersion: textOrNull(value.ffdVersion),
  }
}

/** Ответ `POST /v1/equipment/scan`: кандидаты, ошибки отдельных драйверов и подсказка с портами. */
export function decodeEquipmentScan(raw: unknown): EquipmentScanResult {
  const value = record(raw, 'результат сканирования')
  const candidates = list(value.candidates ?? [], 'найденное оборудование').map((item): EquipmentCandidate => {
    const candidate = record(item, 'найденное устройство')
    const kind = textOrNull(candidate.kind)
    const candidateId = textOrNull(candidate.candidateId)
    if (kind === null || candidateId === null) throw new Error('найденное устройство: нет kind или candidateId')
    return {
      category: driverCategory(candidate.category),
      kind,
      candidateId,
      title: textOrNull(candidate.title) ?? kind,
      connection: textOrNull(candidate.connection) ?? '',
      state: candidateState(candidate.state),
      message: textOrNull(candidate.message),
      suggested: decodeStringMap(candidate.suggestedValues),
      device: decodeCandidateDevice(candidate.device),
      configured: candidate.configured === true,
    }
  })
  const errors = list(value.errors ?? [], 'ошибки сканирования').filter((entry): entry is string => typeof entry === 'string')
  const hints = isRecord(value.hints) ? value.hints : {}
  const comPorts = Array.isArray(hints.comPorts) ? hints.comPorts.filter((port: unknown): port is string => typeof port === 'string') : []
  return { candidates, errors, hints: { comPorts } }
}

/** Ответ `POST /v1/equipment/atol/test`: связь с включённой кассой, слова агента дословно. */
export function decodeAtolTest(raw: unknown): { ok: boolean; message: string } {
  const value = record(raw, 'проверка связи с кассой')
  return { ok: value.ok === true, message: textOrNull(value.message) ?? (value.ok === true ? 'Касса отвечает.' : 'Касса не отвечает.') }
}
